/**
 * 005-packing-verification, admin-packing-oversight bucket. T056 — `GET /oversight/spot-checks`
 * plus `POST /oversight/spot-checks/{id}/reweigh` (FR-040 to FR-042, D-30).
 *
 * **The "configure percentage" half of T056 is a confirmed backend gap, not built here.**
 * `Warehouse:SpotCheckPercentage` (`WarehouseOptions.cs`) is `IOptionsMonitor`-only server
 * configuration, read at the point of use (`SpotCheckSelector.Selected()`) with **no HTTP route
 * anywhere in `OversightController.cs`** to read or change it — verified against source, not
 * assumed. Selection is deliberately server-side, unpredictable and undeclinable (FR-041, DA-07);
 * "the device is not told which packs were chosen" is a real design constraint, but the rate itself
 * having no admin-facing route is a gap, and this file names it rather than fabricating a control
 * that would silently do nothing (constitution I/VI). What the route *does* support — listing
 * selections and recording a re-weigh — is fully wired below.
 */
import { useState } from "react";
import { useLoaderData, useOutletContext, useRevalidator, useSearchParams, type LoaderFunctionArgs } from "react-router-dom";
import toast from "react-hot-toast";
import { Alert, Button, Input } from "rizzui";
import { ErrorSurface, EmptyState, getStatusBadge } from "@steelyard/theme";
import type { ColumnDef } from "@tanstack/react-table";
import { PagedTable, type StatusOption } from "../../components/table/data-table";
import { ListHeader } from "../../components/table/list-header";
import { TableSearch } from "../../components/table/toolbar";
import { can } from "../../lib/permissions";
import { hrefWith } from "../../lib/paging";
import { formatMoment } from "../../lib/format";
import {
  getOversightSpotChecks,
  reweighSpotCheck,
  spotCheckStatusLabel,
  formatQty,
  personName,
  type SpotCheckRow,
  type SpotCheckStatus,
} from "../../lib/services/warehouse";
import type { AccountSummary, PermissionState } from "../../types";

const STATUSES: SpotCheckStatus[] = ["selected", "completed", "skipped"];

/**
 * Read unfiltered once; the status dropdown narrows it here, so each option's count is the number of
 * rows the API itself returned in that status.
 */
export async function spotChecksLoader({ request }: LoaderFunctionArgs) {
  const status = (new URL(request.url).searchParams.get("status") as SpotCheckStatus | null) ?? "";
  return { all: await getOversightSpotChecks(), status };
}

/** "ORD-… · SKU name" — the packing entry as a reader knows it; either half the server could not resolve is "—". */
const entryLabel = (check: SpotCheckRow) => `${check.orderNumber ?? "—"} · ${check.skuName ?? "—"}`;

function ReweighCell({ check, canOverride, onChanged }: { check: SpotCheckRow; canOverride: boolean; onChanged: () => void }) {
  const [value, setValue] = useState("");
  const [pending, setPending] = useState(false);

  if (check.status !== "selected") {
    return check.reweighedValue !== null ? (
      <span className="tabular-nums">
        {formatQty(check.reweighedValue)} kg
        {check.discrepancy !== null ? <span className="text-muted-foreground block text-xs">discrepancy {formatQty(check.discrepancy)} kg</span> : null}
      </span>
    ) : (
      <span className="text-muted-foreground">—</span>
    );
  }

  if (!canOverride) return <span className="text-muted-foreground text-xs">Awaiting a supervisor</span>;

  async function submit() {
    const parsed = Number(value);
    if (!value.trim() || !Number.isFinite(parsed) || parsed <= 0) {
      toast.error("Enter a positive re-weigh value");
      return;
    }
    setPending(true);
    try {
      await reweighSpotCheck(check.id, parsed);
      toast.success("Re-weigh recorded");
      onChanged();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not record the re-weigh");
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="flex items-center gap-2">
      <Input
        aria-label={`Re-weighed value for ${entryLabel(check)}`}
        placeholder="kg"
        inputMode="decimal"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        className="w-24"
      />
      <Button size="sm" disabled={pending} onClick={submit}>
        {pending ? "Recording…" : "Record re-weigh"}
      </Button>
    </div>
  );
}

/** Accessors are what the search box matches and a header click sorts — the whole set, since there is no paging here. */
function spotCheckColumns(canOverride: boolean, onChanged: () => void): ColumnDef<SpotCheckRow>[] {
  return [
    { id: "selectedAt", accessorKey: "selectedAt", header: "Selected", cell: ({ row }) => <span className="text-gray-600">{formatMoment(row.original.selectedAt)}</span> },
    { id: "packingEntry", accessorFn: entryLabel, header: "Packing entry", cell: ({ row }) => entryLabel(row.original) },
    { id: "packedBy", accessorFn: (row) => personName(row.packerName), header: "Packer", cell: ({ row }) => personName(row.original.packerName) },
    { id: "status", accessorKey: "status", header: "Status", cell: ({ row }) => getStatusBadge(row.original.status) },
    {
      id: "supervisorId",
      accessorFn: (row) => (row.supervisorId ? personName(row.supervisorName) : ""),
      header: "Supervisor",
      cell: ({ row }) => (row.original.supervisorId ? personName(row.original.supervisorName) : "—"),
    },
    { id: "reweigh", header: "Re-weigh", cell: ({ row }) => <ReweighCell check={row.original} canOverride={canOverride} onChanged={onChanged} /> },
  ];
}

export default function SpotChecksPage() {
  const { all, status } = useLoaderData() as Awaited<ReturnType<typeof spotChecksLoader>>;
  const { permissionState } = useOutletContext<{ account: AccountSummary; permissionState: PermissionState }>();
  const [searchParams] = useSearchParams();
  const revalidator = useRevalidator();
  const canOverride = can(permissionState, "packing.override");
  const checks = status ? all.filter((check) => check.status === status) : all;
  const statusHref = (next?: string) => hrefWith("/packing/spot-checks", searchParams, { status: next, page: undefined });
  const statusOptions: StatusOption[] = [
    { label: "All spot-checks", href: statusHref(undefined), active: !status, count: all.length },
    ...STATUSES.map((s) => ({
      label: spotCheckStatusLabel(s),
      href: statusHref(s),
      active: s === status,
      count: all.filter((check) => check.status === s).length,
      status: s,
    })),
  ];

  if (!can(permissionState, "packing.oversight")) {
    return (
      <ErrorSurface
        error={{ title: "Not permitted", status: 403, detail: "Spot-checks are gated by packing.oversight, which your role does not hold." }}
        action="view spot-checks"
      />
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <ListHeader
        title="Spot-Checks"
        count={checks.length}
        description="Randomly selected completed packs awaiting a supervisor re-weigh. Selection happens server-side, at completion, and cannot be predicted or declined."
        breadcrumb={[{ name: "Home", href: "/" }, { name: "Packing" }, { name: "Spot-Checks" }]}
      />

      <Alert color="warning" variant="flat">
        The selection rate is set on the server, with no control here to read or change it — this
        screen lists selections and records re-weighs; it cannot offer a percentage control that
        doesn&apos;t exist on the API.
      </Alert>

      {/* No paging on this route (`status?` only), so PagedTable pages the whole set in memory. */}
      <PagedTable<SpotCheckRow>
        result={{ items: checks, page: 1, pageSize: checks.length, totalCount: checks.length }}
        caption="Spot-checks, with packing entry, who packed it, status, supervisor and re-weigh"
        columns={spotCheckColumns(canOverride, () => revalidator.revalidate())}
        getRowId={(row) => row.id}
        status={{ label: "Status", options: statusOptions }}
        search={(table) => <TableSearch table={table} placeholder="Search spot checks…" />}
        clear={{ active: Boolean(status), href: statusHref(undefined) }}
        empty={<EmptyState title="No spot-checks">{status ? `Nothing is currently "${status}".` : "No pack has been selected for a spot-check yet."}</EmptyState>}
      />
    </div>
  );
}
